import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import { U2000ApiValueToDisplayYmd } from '../u2000Core/tools/U2000DateTimeHelpers';
import { unmaskPhone } from '../u2000Core/tools/U2000MaskUtils';
import { u2106ResolveChangePasswordErrorMessage } from './tools/U2106ChangePasswordErrors';
import {
  u2106IsMfaErrorCode,
  u2106ResolveMfaErrorMessage,
} from './tools/U2106MfaErrors';
import {
  u2106IsProfileErrorCode,
  u2106ResolveProfileErrorMessage,
} from './tools/U2106ProfileErrors';

const LOGICALUNIT_URL = 'U2106';

export type U2106ChangePasswordPayload = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export type U2106ChangePasswordResult = {
  success: boolean;
};

export type U2106EmployeeProfile = {
  id: string;
  version: number;
  codeEmployee: string | null;
  numEmployee: string | null;
  firstName: string;
  lastName: string;
  addressLine1: string;
  city: string;
  postalCode: string;
  birthDate: string | null;
  hideBirthdayInCalendar: boolean;
  phoneNumber: string | null;
  cellPhone: string | null;
  otherPhone: string | null;
  otherPhoneExt: string | null;
  otherPhoneComment: string | null;
  /** Courriel personnel (employé) — distinct de userEmail compte. */
  email: string | null;
};

export type U2106HourBanks = {
  vacationBncHours: number;
  vacationBncHoursInitial: number;
  vacationNegativeThresholdHours: number;
  holidayBncHours: number;
  holidayBncHoursInitial: number;
  sickLeaveBncHours: number;
  sickLeaveBncHoursInitial: number;
  specialBncHours: number;
  specialBncHoursInitial: number;
};

/** Réponse GET /U2106/profile (BL-0338 + BL-0340 + BL-0413). */
export type U2106ProfileResult = {
  hasOrganization: boolean;
  hasEmployee: boolean;
  /** Courriel compte (sys_user) — Identité. */
  userEmail: string;
  userVersion: number;
  userFirstName: string;
  userLastName: string;
  employee: U2106EmployeeProfile | null;
  hourBanks: U2106HourBanks | null;
  /** BL-0413 — préférence MFA active. */
  mfaEnabled?: boolean;
  /** BL-0413 — secret TOTP présent (enrôlé). */
  mfaEnrolled?: boolean;
  /** BL-0520 — compte Google exclusif : masquer MDP et MFA. */
  ssoEnabled?: boolean;
};

/** BL-0413 — statut MFA. */
export type U2106MfaStatusResult = {
  enabled: boolean;
  enrolled: boolean;
  enrolledAt: string | null;
  /** BL-0523 — false = MFA local sans objet (compte SSO). */
  applicable: boolean;
};

export type U2106MfaEnrollStartResult = {
  otpauthUri: string;
  manualSecret: string;
};

export type U2106MfaBackupCodesResult = {
  backupCodes: string[];
};

export type U2106MfaDisablePayload = {
  password?: string;
  totpCode?: string;
};

export type U2106MfaProofPayload = U2106MfaDisablePayload;

/** UPDATE mode usager seul (pas de version employé). */
export type U2106ProfileUpdateUserPayload = {
  userEmail: string;
  userVersion: number;
  firstName: string;
  lastName: string;
};

/**
 * UPDATE mode employé.
 * userFirstName/userLastName → sys_user ; firstName/lastName → em_employee (indépendants).
 */
export type U2106ProfileUpdateEmployeePayload = {
  version: number;
  userEmail: string;
  userVersion: number;
  userFirstName: string;
  userLastName: string;
  firstName: string;
  lastName: string;
  addressLine1: string;
  city: string;
  postalCode: string;
  birthDate: string | null;
  hideBirthdayInCalendar: boolean;
  phoneNumber: string | null;
  cellPhone: string | null;
  otherPhone: string | null;
  otherPhoneExt: string | null;
  otherPhoneComment: string | null;
  email: string | null;
};

export type U2106ProfileUpdatePayload =
  | U2106ProfileUpdateUserPayload
  | U2106ProfileUpdateEmployeePayload;

/** Axe quota (usager ou groupe) — GET /U2106/aiUsage (BL-0246). */
export type U2106QuotaAxis = {
  usageTokens: number;
  quotaMonthlyTokens: number | null;
  usagePercent: number | null;
};

export type U2106AiUsageProviderRef = {
  id: string;
  code: string;
  name: string;
};

export type U2106AiUsageProvider = {
  provider: U2106AiUsageProviderRef;
  user: U2106QuotaAxis;
  group: U2106QuotaAxis;
  blocked: boolean;
  blockReason: 'user' | 'group' | null;
};

export type U2106AiUsageUserGroup = {
  id: string;
  code: string;
  name: string;
};

export type U2106AiUsageResult = {
  hasAiGroup: boolean;
  userGroup: U2106AiUsageUserGroup | null;
  period: {
    start: string;
    timezone: string;
  };
  providers: U2106AiUsageProvider[];
  anyBlocked: boolean;
};

/** Formulaire édition (noms usager et employé indépendants). */
export type U2106ProfileFormRow = {
  userEmail: string;
  userVersion: number;
  userFirstName: string;
  userLastName: string;
  version: number;
  /** Prénom / nom employé. */
  firstName: string;
  lastName: string;
  addressLine1: string;
  city: string;
  postalCode: string;
  birthDate: string | null;
  hideBirthdayInCalendar: boolean;
  phoneNumber: string;
  cellPhone: string;
  otherPhone: string;
  otherPhoneExt: string;
  otherPhoneComment: string;
  /** Courriel personnel (employé). */
  email: string;
};

const LogicalUnitEndpoints = {
  CHANGE_PASSWORD: `${LOGICALUNIT_URL}/password/change`,
  GET_PROFILE: `${LOGICALUNIT_URL}/profile`,
  UPDATE_PROFILE: `${LOGICALUNIT_URL}/profile/update`,
  GET_SECRET_KEY: `${LOGICALUNIT_URL}/secretkey`,
  GET_AI_USAGE: `${LOGICALUNIT_URL}/aiUsage`,
  MFA_STATUS: `${LOGICALUNIT_URL}/mfa/status`,
  MFA_ENROLL_START: `${LOGICALUNIT_URL}/mfa/enroll/start`,
  MFA_ENROLL_CONFIRM: `${LOGICALUNIT_URL}/mfa/enroll/confirm`,
  MFA_DISABLE: `${LOGICALUNIT_URL}/mfa/disable`,
  MFA_BACKUP_REGENERATE: `${LOGICALUNIT_URL}/mfa/backup-codes/regenerate`,
} as const;

/** POST changement de mot de passe self-service (BL-0245). */
export const doChangePassword = (axios: AxiosInstance, payload: U2106ChangePasswordPayload) => {
  return axios.post<{ result: U2106ChangePasswordResult }>(LogicalUnitEndpoints.CHANGE_PASSWORD, payload);
};

/** GET profil self-service (BL-0244 / BL-0338 / BL-0340). */
export const doGetProfile = (axios: AxiosInstance) => {
  return axios.get<{ result: U2106ProfileResult }>(LogicalUnitEndpoints.GET_PROFILE);
};

/** GET clé secrète usager connecté (self-service, BL-0402) — consultation auditée. */
export const doGetSecretKey = (axios: AxiosInstance) => {
  return axios.get<{ result: { id: string; secretKey: string } }>(LogicalUnitEndpoints.GET_SECRET_KEY);
};

/** POST mise à jour profil self-service (BL-0244 / BL-0338 / BL-0340). */
export const doUpdateProfile = (axios: AxiosInstance, payload: U2106ProfileUpdatePayload) => {
  return axios.post<{ result: U2106ProfileResult }>(LogicalUnitEndpoints.UPDATE_PROFILE, payload);
};

/** GET consommation IA personnelle du mois civil org (BL-0246). */
export const doGetAiUsage = (axios: AxiosInstance) => {
  return axios.get<{ result: U2106AiUsageResult }>(LogicalUnitEndpoints.GET_AI_USAGE);
};

/** GET statut MFA (BL-0413). */
export const doGetMfaStatus = (axios: AxiosInstance) => {
  return axios.get<{ result: U2106MfaStatusResult }>(LogicalUnitEndpoints.MFA_STATUS);
};

/** POST démarrage enrôlement MFA — otpauthUri + secret manuel (une fois). */
export const doMfaEnrollStart = (axios: AxiosInstance) => {
  return axios.post<{ result: U2106MfaEnrollStartResult }>(LogicalUnitEndpoints.MFA_ENROLL_START);
};

/** POST confirmation enrôlement MFA — codes de secours (une fois). */
export const doMfaEnrollConfirm = (axios: AxiosInstance, payload: { code: string }) => {
  return axios.post<{ result: U2106MfaBackupCodesResult }>(
    LogicalUnitEndpoints.MFA_ENROLL_CONFIRM,
    payload,
  );
};

/** POST désactivation MFA (preuve MDP ou TOTP). */
export const doMfaDisable = (axios: AxiosInstance, payload: U2106MfaDisablePayload) => {
  return axios.post<{ result: { success: boolean } }>(LogicalUnitEndpoints.MFA_DISABLE, payload);
};

/** POST régénération codes de secours (preuve MDP ou TOTP). */
export const doMfaRegenerateBackupCodes = (axios: AxiosInstance, payload: U2106MfaProofPayload) => {
  return axios.post<{ result: U2106MfaBackupCodesResult }>(
    LogicalUnitEndpoints.MFA_BACKUP_REGENERATE,
    payload,
  );
};

export function emptyU2106ProfileFormRow(): U2106ProfileFormRow {
  return {
    userEmail: '',
    userVersion: 0,
    userFirstName: '',
    userLastName: '',
    version: 0,
    firstName: '',
    lastName: '',
    addressLine1: '',
    city: '',
    postalCode: '',
    birthDate: null,
    hideBirthdayInCalendar: false,
    phoneNumber: '',
    cellPhone: '',
    otherPhone: '',
    otherPhoneExt: '',
    otherPhoneComment: '',
    email: '',
  };
}

/** Champs employé du formulaire (sans noms usager). */
export function u2106EmployeeToFormRow(employee: U2106EmployeeProfile): Pick<
  U2106ProfileFormRow,
  | 'version'
  | 'firstName'
  | 'lastName'
  | 'addressLine1'
  | 'city'
  | 'postalCode'
  | 'birthDate'
  | 'hideBirthdayInCalendar'
  | 'phoneNumber'
  | 'cellPhone'
  | 'otherPhone'
  | 'otherPhoneExt'
  | 'otherPhoneComment'
  | 'email'
> {
  return {
    version: employee.version,
    firstName: employee.firstName ?? '',
    lastName: employee.lastName ?? '',
    addressLine1: employee.addressLine1 ?? '',
    city: employee.city ?? '',
    postalCode: employee.postalCode ?? '',
    birthDate: U2000ApiValueToDisplayYmd(employee.birthDate),
    hideBirthdayInCalendar: employee.hideBirthdayInCalendar === true,
    phoneNumber: employee.phoneNumber ?? '',
    cellPhone: employee.cellPhone ?? '',
    otherPhone: employee.otherPhone ?? '',
    otherPhoneExt: employee.otherPhoneExt ?? '',
    otherPhoneComment: employee.otherPhoneComment ?? '',
    email: employee.email ?? '',
  };
}

/**
 * Hydrate le formulaire : Identité = champs user* ;
 * Employé = firstName/lastName etc. (indépendants).
 */
export function u2106ProfileToFormRow(result: U2106ProfileResult): U2106ProfileFormRow {
  const userFields = {
    userEmail: result.userEmail ?? '',
    userVersion: result.userVersion ?? 0,
    userFirstName: result.userFirstName ?? '',
    userLastName: result.userLastName ?? '',
  };

  if (result.hasEmployee && result.employee) {
    return {
      ...emptyU2106ProfileFormRow(),
      ...userFields,
      ...u2106EmployeeToFormRow(result.employee),
    };
  }

  return {
    ...emptyU2106ProfileFormRow(),
    ...userFields,
  };
}

/**
 * Construit le body UPDATE.
 * - Mode usager : firstName/lastName = noms usager (userFirstName/userLastName).
 * - Mode employé : userFirstName/userLastName + firstName/lastName employé + whitelist.
 */
export function u2106FormRowToUpdatePayload(
  row: U2106ProfileFormRow,
  hasEmployee: boolean,
): U2106ProfileUpdatePayload {
  if (!hasEmployee) {
    return {
      userEmail: row.userEmail.trim(),
      userVersion: row.userVersion,
      firstName: row.userFirstName.trim(),
      lastName: row.userLastName.trim(),
    };
  }

  const birth = row.birthDate;
  const birthDate =
    birth === undefined || birth === null || birth === '' ? null : String(birth).slice(0, 10);

  return {
    version: row.version,
    userEmail: row.userEmail.trim(),
    userVersion: row.userVersion,
    userFirstName: row.userFirstName.trim(),
    userLastName: row.userLastName.trim(),
    firstName: row.firstName.trim(),
    lastName: row.lastName.trim(),
    addressLine1: row.addressLine1.trim(),
    city: row.city.trim(),
    postalCode: row.postalCode.trim(),
    birthDate,
    hideBirthdayInCalendar: row.hideBirthdayInCalendar === true,
    phoneNumber: nullIfEmptyPhone(row.phoneNumber),
    cellPhone: nullIfEmptyPhone(row.cellPhone),
    otherPhone: nullIfEmptyPhone(row.otherPhone),
    otherPhoneExt: nullIfEmpty(row.otherPhoneExt),
    otherPhoneComment: nullIfEmpty(row.otherPhoneComment),
    email: nullIfEmpty(row.email),
  };
}

/** True si les données formulaire courantes diffèrent de la baseline (dirty). */
export function u2106IsProfileFormDirty(
  current: U2106ProfileFormRow | null | undefined,
  old: U2106ProfileFormRow | null | undefined,
): boolean {
  if (!current || !old) {
    return false;
  }
  return JSON.stringify(current) !== JSON.stringify(old);
}

function nullIfEmpty(value: string | null | undefined): string | null {
  if (value === undefined || value === null) {
    return null;
  }
  const trimmed = String(value).trim();
  return trimmed === '' ? null : trimmed;
}

/** BL-0440 — chiffres seuls (masque UI) ; vide → null (BL-0420). */
function nullIfEmptyPhone(value: string | null | undefined): string | null {
  return nullIfEmpty(unmaskPhone(value ?? ''));
}

/** Mapping erreurs métier U2106 pour U2000HandleApiError (password + profil + MFA). */
export function luHandleAppiError(
  t: TranslateFn,
  nameSpace: string,
  err: { errorCode?: string; description?: string },
  errorResult: U2000ApiErrorResult,
): void {
  const code = err.errorCode;
  if (!code?.startsWith('BU2106')) {
    return;
  }

  let text: string;
  if (u2106IsMfaErrorCode(code)) {
    text = u2106ResolveMfaErrorMessage(err, t, nameSpace);
  } else if (u2106IsProfileErrorCode(code)) {
    text = u2106ResolveProfileErrorMessage(err, t, nameSpace);
  } else {
    text = u2106ResolveChangePasswordErrorMessage(err, t, nameSpace);
  }

  errorResult.genericErrors.push({
    title: t('errorTitle'),
    text,
  });
}
